<!-- @format -->

<template>
	<!-- 代码输入框 -->
	<div ref="editorform" style="min-height: 300px; width: 100%" class="ace-editor"></div>
</template>
<script lang="ts" setup>
	import beautify from 'js-beautify';
	import type { Ace } from 'ace-builds';
	import { dynamicLoadByName, getCDNLinkPath } from '@/utils';

	interface Props {
		value: string;
		readonly?: boolean;
		language?: string;
		theme?: string;
		height?: number;
		snippets?: any;
		options?: any;
	}

	const props = withDefaults(defineProps<Props>(), {
		language: 'javascript',
		theme: 'sqlserver',
		snippets: [],
	});

	const emits = defineEmits(['update:value', 'onload']);

	const editorform = ref<HTMLElement | null>(null);

	const value = computed({
		get() {
			return props.value;
		},
		set(value) {
			emits('update:value', value);
		},
	});

	// ace实例
	const editor = ref<Ace.Editor>();

	const aceOptions = computed(() => ({
		minLines: 5, // 最小行数，还未到最大行数时，编辑器会自动伸缩大小
		fontSize: 12, // 编辑器内字体大小
		tabSize: 2,
		showPrintMargin: false,
		highlightActiveLine: true,
		readOnly: props.readonly ? props.readonly : false,
		useWorker: false,
		...props.options,
	}));

	const addAutoCompletion = (ace) => {
		const langTools = ace.require('ace/ext/language_tools');
		langTools.addCompleter({
			getCompletions: (_, __, ___, prefix, callback) => {
				if (prefix.length === 0) {
					return callback(null, []);
				} else {
					return callback(null, props.snippets);
				}
			},
		});
	};

	const initialize = async () => {
		await dynamicLoadByName('ace-builds');
		const ace = window['ace'] || window['ace-builds'];

		const basePath = getCDNLinkPath()
			.replace(/\{name\}/g, 'ace-builds')
			.replace(/\{path\}/g, 'src-min-noconflict/');

		// 初始化
		editor.value = ace.edit(editorform.value as Element, aceOptions.value);

		ace.config.set('basePath', basePath);

		editor.value!.setValue(value.value);

		addAutoCompletion(ace); //添加自定义代码提示！！

		editor.value!.setOptions({
			enableBasicAutocompletion: true,
			enableSnippets: true, // 设置代码片段提示
			enableLiveAutocompletion: true, // 设置自动提示
		});

		ace.config.setModuleUrl(`ace/mode/${props.language}`, `${basePath}mode-${props.language}.js`);

		ace.config.setModuleUrl(`ace/theme/${props.theme}`, `${basePath}theme-${props.theme}.js`);

		// 代码语言格式
		editor.value!.session.setMode(`ace/mode/${props.language}`);
		// 设置皮肤
		editor.value!.setTheme(`ace/theme/${props.theme}`);
		//自动换行
		editor.value!.getSession().setUseWrapMode(true);
		// 支持双向绑定
		editor.value!.on('change', () => {
			value.value = editor.value!.getValue();
		});
		//快捷键
		editor.value!.commands.addCommand({
			name: '格式化',
			bindKey: { win: 'Ctrl-Shift-F', mac: 'Command-Shift-F' },
			exec: () => {
				const beautifyValue = beautify(editor.value!.getValue(), {
					indent_size: 2,
				});
				editor.value!.setValue(beautifyValue);
			},
		});

		emits('onload', editor.value);
	};

	onMounted(() => {
		initialize();
	});

	onUnmounted(() => {
		editor.value && editor.value.destroy();
	});

	defineExpose({ editor });
</script>
<style lang="scss" scoped></style>
